import type { CSSProperties, MouseEvent } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import clsx from 'clsx';
import { Badge } from '../../catalyst/badge';
import type { ViewNode } from './graphModel';
import type { Row } from './graphTree';
import { kindIcon } from './kindIcon';
import { MentionCount } from './nodes/MentionCount';

/** The indent of each tree level, in px. */
const INDENT = 20;
/** The row height; a windowed tree places its rows this far apart. Matches `h-9`. */
export const TREE_ROW_HEIGHT = 36;

const count = (value: number) => value.toLocaleString('en-US');

/** The muted line beside a row's label: what the canvas card shows under its title. */
function detailOf(node: ViewNode | undefined, row: Row): string | null {
  switch (node?.kind) {
    case 'engine':
      return node.engineType ?? null;
    case 'part':
      return node.description ?? null;
    case 'reference':
      // A task label already names its description.
      return node.refKind === 'task' ? null : (node.title ?? null);
    case 'document':
      return node.category;
    case 'group':
      if (!row.expanded || !row.group) return null;
      return row.group.shown >= row.group.total
        ? 'all shown'
        : `${count(row.group.shown)} of ${count(row.group.total)} shown`;
    default:
      return null;
  }
}

type Props = {
  row: Row;
  /** The row's node; none for a "Show 30 more" row. */
  node: ViewNode | undefined;
  /** An entity's mention count (see `cardMentions`). */
  mentions: number | undefined;
  selected: boolean;
  /** The one row Tab reaches (roving tabindex). */
  tabbable: boolean;
  onClick: () => void;
  /** The chevron: opens or closes the row without selecting it. Absent for the root, which always shows its children. */
  onToggle?: () => void;
  /** A windowed tree's placement of the row. */
  style?: CSSProperties;
  className?: string;
};

/** One row of the list mode's tree: chevron, kind icon, label, detail, and the counts the canvas card shows. */
export function TreeRow({ row, node, mentions, selected, tabbable, onClick, onToggle, style, className }: Props) {
  const more = row.kind === 'more';
  const { icon: Icon, className: iconClassName } = kindIcon(row.kind);
  const Chevron = row.expanded ? ChevronDownIcon : ChevronRightIcon;
  const detail = detailOf(node, row);
  const toggle = (event: MouseEvent) => {
    // The row's own click would select it.
    event.stopPropagation();
    onToggle?.();
  };
  return (
    <div
      role="treeitem"
      data-row-id={row.id}
      aria-level={row.depth}
      aria-setsize={row.setSize}
      aria-posinset={row.posInSet}
      aria-expanded={row.expandable ? row.expanded : undefined}
      aria-selected={more ? undefined : selected}
      tabIndex={tabbable ? 0 : -1}
      // The indent grows with the depth, so it is an inline style.
      style={{ ...style, paddingLeft: (row.depth - 1) * INDENT + 8 }}
      className={clsx(
        'flex h-9 cursor-pointer items-center gap-2 rounded-md pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset',
        selected
          ? 'bg-indigo-50 text-indigo-950 dark:bg-indigo-500/15 dark:text-indigo-50'
          : 'text-zinc-900 hover:bg-zinc-50 dark:text-zinc-100 dark:hover:bg-zinc-800',
        className,
      )}
      onClick={onClick}
    >
      {/* Every row keeps the chevron's slot, so labels of one level line up. */}
      <span className="flex size-4 shrink-0 items-center justify-center">
        {row.expandable && (
          <Chevron
            aria-hidden
            className={clsx('size-4 text-zinc-400 dark:text-zinc-500', onToggle && 'hover:text-zinc-700 dark:hover:text-zinc-200')}
            onClick={onToggle ? toggle : undefined}
          />
        )}
      </span>
      {!more && <Icon aria-hidden className={clsx('size-4 shrink-0', iconClassName)} />}
      <span
        className={clsx(
          'min-w-0 shrink truncate',
          more ? 'font-medium text-indigo-600 dark:text-indigo-400' : 'font-medium',
        )}
        title={row.label}
      >
        {row.label}
      </span>
      {row.group && <Badge color="zinc">{count(row.group.total)}</Badge>}
      {detail && (
        <span className="min-w-0 flex-1 truncate text-xs text-zinc-500 dark:text-zinc-400" title={detail}>
          {detail}
        </span>
      )}
      <MentionCount count={mentions} />
    </div>
  );
}
